import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"; // Pastikan component Tabs sudah ada
import { useTahun } from "@/hooks/useTahun";
import {
  getDataRekapPendaftaranKembali,
  getDataRekapPendaftaranPerbandinganTahunan,
} from "@/services/getData";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, FileText, LayoutList, Users } from "lucide-react";
import { useState } from "react";
import { LuListFilter as LuListFilterIcon } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";
import { AdmissionStatusChart } from "./partial/AdmissionStatusChart";
import { ComparisonYearlyChart } from "./partial/ComparisonYearlyChart";
import { ComparisonYearlyLineChart } from "./partial/ComparisonYearlyLineChart";
import { FacultyInterestChart } from "./partial/FacultyInterestChart";
import FormFilter from "./partial/FormFilter";
import { FunnelChart } from "./partial/FunnelChart";
import { PaymentProgressChart } from "./partial/PaymentProgressChart";
import TabelRekap from "./partial/TabelRekap";
import TableRekapPenerimaan from "./partial/TableRekapPenerimaan";

export default function DashboardPimpinanPage() {
  const { selectedTahun } = useTahun();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ambil initial state dari URL
  const initialMenu = searchParams.get("menu") === "DATA" ? "DATA" : "GRAFIK";
  const initialSubmenu =
    searchParams.get("submenu") === "PENERIMAAN"
      ? "PENERIMAAN"
      : "DAFTARKEMBALI";

  const [activeMenu, setActiveMenu] = useState<"GRAFIK" | "DATA">(initialMenu);
  const [activeSubmenu, setActiveSubmenu] = useState<
    "DAFTARKEMBALI" | "PENERIMAAN"
  >(initialSubmenu);

  const [showFilter, setShowFilter] = useState(false);

  const idsUkParams = searchParams.get("ids_uk") || "";
  const idsFakultasParams = searchParams.get("ids_fakultas") || "";
  const idsJalurJenisParams = searchParams.get("ids_ref_jalur_jenis") || "";

  const {
    data: dataRekapPendaftaran,
    isPending: isPendingDataRekapPendaftaran,
  } = useQuery({
    queryKey: [
      "dataRekapPendaftaran",
      idsJalurJenisParams,
      idsUkParams,
      selectedTahun,
      idsFakultasParams,
    ],
    queryFn: () =>
      getDataRekapPendaftaranKembali(
        idsJalurJenisParams,
        idsUkParams,
        selectedTahun?.label,
        idsFakultasParams,
      ),
  });

  const {
    data: dataRekapPerbandinganTahunan,
    isPending: isPendingDataRekapPerbandinganTahunan,
  } = useQuery({
    queryKey: [
      "dataRekapPerbandinganTahunan",
      idsJalurJenisParams,
      idsUkParams,
      selectedTahun,
      idsFakultasParams,
    ],
    queryFn: () =>
      getDataRekapPendaftaranPerbandinganTahunan(
        idsJalurJenisParams,
        idsUkParams,
        selectedTahun?.label,
        idsFakultasParams,
      ),
  });

  const isFilterActive =
    !!idsJalurJenisParams || !!idsUkParams || !!idsFakultasParams;

  const handleTabChange = (value: string) => {
    const menu = value as "GRAFIK" | "DATA";
    setActiveMenu(menu);

    const newParams = new URLSearchParams(searchParams);
    newParams.set("menu", menu);
    setSearchParams(newParams);
  };

  const handleSubmenuChange = (value: "DAFTARKEMBALI" | "PENERIMAAN") => {
    setActiveSubmenu(value);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("ids_ref_jalur_jenis");
    newParams.delete("ids_uk");
    newParams.delete("ids_fakultas");
    newParams.set("submenu", value);
    setSearchParams(newParams);
  };

  return (
    <div className="space-y-6 min-h-screen pb-10">
      {/* --- HEADER SECTION --- */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Ringkasan data dan grafik penerimaan mahasiswa baru.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
            Tahun Akademik:
          </span>
          <span className="text-sm font-bold text-slate-900 dark:text-white">
            {selectedTahun ? selectedTahun?.label : "-"}
          </span>
        </div>
      </div>

      {/* --- MAIN TABS --- */}
      <Tabs
        defaultValue={activeMenu}
        onValueChange={handleTabChange}
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px] bg-slate-100 dark:bg-slate-900 p-1">
          <TabsTrigger
            value="GRAFIK"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm transition-all"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Visualisasi Grafik
          </TabsTrigger>
          <TabsTrigger
            value="DATA"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm transition-all"
          >
            <LayoutList className="w-4 h-4 mr-2" />
            Data Tabel
          </TabsTrigger>
        </TabsList>

        {/* CONTENT: GRAFIK */}
        <TabsContent
          value="GRAFIK"
          className="space-y-4 animate-in fade-in-50 duration-300"
        >
          <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Statistik Pendaftaran
              </CardTitle>
              <CardDescription>
                Grafik perbandingan pendaftar berdasarkan jalur masuk tahun{" "}
                {selectedTahun?.label}.
              </CardDescription>
            </CardHeader>
            <CardContent className="pl-2">
              <div className="grid grid-cols-2 gap-5">
                <AdmissionStatusChart
                  data={dataRekapPendaftaran}
                  isLoading={isPendingDataRekapPendaftaran}
                />

                <FunnelChart
                  data={dataRekapPendaftaran}
                  isLoading={isPendingDataRekapPendaftaran}
                />

                <PaymentProgressChart
                  data={dataRekapPendaftaran}
                  isLoading={isPendingDataRekapPendaftaran}
                />

                <FacultyInterestChart
                  data={dataRekapPendaftaran}
                  isLoading={isPendingDataRekapPendaftaran}
                />

                {/* <BarChartComponent
                  tahun={
                    selectedTahun
                      ? selectedTahun?.label
                      : new Date().getFullYear()
                  }
                /> */}

                {/* <OccupancyChart
                  data={dataRekapPendaftaran}
                  isLoading={isPendingDataRekapPendaftaran}
                /> */}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  Perbandingan & Tren Tahunan
                </CardTitle>
                <CardDescription>
                  Analisis perbandingan data pendaftaran antar tahun akademik.
                </CardDescription>
              </div>
              <Button
                variant={isFilterActive ? "default" : "outline"}
                size="sm"
                className={`flex items-center gap-2 transition-all ${
                  isFilterActive
                    ? ""
                    : "border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                }`}
                onClick={() => setShowFilter(true)}
              >
                <LuListFilterIcon className="w-4 h-4" />
                <span>Filter Grafik</span>
                {isFilterActive && (
                  <span className="flex h-2 w-2 rounded-full bg-white ml-1 animate-pulse" />
                )}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-5">
                <div className="col-span-1">
                  <ComparisonYearlyChart
                    data={dataRekapPerbandinganTahunan}
                    isLoading={isPendingDataRekapPerbandinganTahunan}
                  />
                </div>
                <div className="col-span-1">
                  <ComparisonYearlyLineChart
                    data={dataRekapPerbandinganTahunan}
                    isLoading={isPendingDataRekapPerbandinganTahunan}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Placeholder for Area Chart if needed later */}
          {/* <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
             <Card className="col-span-4">...</Card>
          </div> */}
        </TabsContent>

        {/* CONTENT: DATA */}
        <TabsContent
          value="DATA"
          className="space-y-6 animate-in fade-in-50 duration-300"
        >
          {/* SUBMENU NAVIGATION (Segmented Control Style) */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-2 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="px-2">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Pilih Kategori Data:
              </span>
            </div>
            <div className="flex p-1 bg-slate-100 dark:bg-slate-950 rounded-lg w-full sm:w-auto">
              <button
                onClick={() => handleSubmenuChange("DAFTARKEMBALI")}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeSubmenu === "DAFTARKEMBALI"
                    ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
              >
                <FileText className="w-4 h-4" />
                Rekap Daftarkembali
              </button>
              <button
                onClick={() => handleSubmenuChange("PENERIMAAN")}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeSubmenu === "PENERIMAAN"
                    ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
              >
                <Users className="w-4 h-4" />
                Rekap Penerimaan
              </button>
            </div>
          </div>

          {/* TABLE CONTENT RENDERER */}
          <div className="min-h-[500px]">
            {activeSubmenu === "DAFTARKEMBALI" && <TabelRekap />}
            {activeSubmenu === "PENERIMAAN" && <TableRekapPenerimaan />}
          </div>
        </TabsContent>
      </Tabs>

      {/* MODAL FILTER */}
      <Modal
        show={showFilter}
        onClose={() => setShowFilter(false)}
        width="2xl"
        className="dark:bg-slate-950 dark:border-slate-800"
      >
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  );
}
